'use client';

import { useMemoizedFn } from 'ahooks';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { createFileItem } from '../_utils';
import { fetchFileList } from '../apis';
import type { BreadcrumbItem, FileItem, OrderBy, SortBy } from '../types';
import type { FileFilters } from './use-file-filters';
import { useOpenFile } from './use-open-file';

interface UseFileNavigationParams {
  urlFolderId?: string;
  search?: string;
  sortBy?: SortBy;
  order?: OrderBy;
  filters: FileFilters;
  itemsPerRow: number;
}

// 每页显示的行数
const ROWS_PER_PAGE = 8;

// 格式化开始日期为 API 所需的 ISO UTC 格式
const formatStartDateForApi = (date?: Date): string | undefined => {
  if (!date) return;

  const dateToFormat = new Date(date);
  dateToFormat.setHours(0, 0, 0, 0);
  return dateToFormat.toISOString();
};

// 格式化结束日期为 API 所需的 ISO UTC 格式
const formatEndDateForApi = (date?: Date): string | undefined => {
  if (!date) return;

  const dateToFormat = new Date(date);
  dateToFormat.setHours(23, 59, 59, 999);
  return dateToFormat.toISOString();
};

const transformTaskTypes = (taskTypes: FileFilters['taskTypes']): FileFilters['taskTypes'] => {
  if (!taskTypes || taskTypes.length === 0) return taskTypes;

  const result = [...taskTypes];
  const extendIndex = result.indexOf('extend');

  if (extendIndex !== -1) {
    // 去掉 'extend'
    result.splice(extendIndex, 1);
    result.push(...['video2extend' as const, 'video2extail' as const]);
  }

  // 筛选图生，要同时筛选首尾帧和多帧
  if (taskTypes.includes('img2video')) {
    result.push('headtailimg2video');
    result.push('multi_frame');
  }

  return result;
};

export function useFileNavigation({
  urlFolderId,
  search = '',
  sortBy = 'last_open_at',
  order = 'newest',
  filters,
  itemsPerRow = 4,
}: UseFileNavigationParams) {
  const t = useTranslations();
  const teamSpaceId = useStore((state) => state.teamSpaceInfo?.space_id);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const pageToken = useRef<string>('');
  const router = useRouter();
  const [currentFolderId, setCurrentFolderId] = useState<string>(urlFolderId || 'root');
  const rootFolder = useMemo(() => breadcrumbs[0], [breadcrumbs]); // 面包屑第一层是根文件夹
  const pageSize = useMemo(() => itemsPerRow * ROWS_PER_PAGE, [itemsPerRow]);

  // 加载文件列表
  const { run: loadFiles, loading: isLoading } = useFetch(
    (isLoadMore: boolean) =>
      fetchFileList({
        query: {
          success_only: true,
          'created_at.from': formatStartDateForApi(filters.dateRange?.from),
          'created_at.to': formatEndDateForApi(filters.dateRange?.to),
          owner_id: filters.creatorIds?.[0],
          filter_type: filters.fileTypes,
          is_marked: filters.filterMark,
          model_version: filters.modelVersions,
          order_by: sortBy === 'last_open_at' ? 'newest' : order, // 按“最近打开”都是最新在前
          'pager.pagesz': pageSize,
          'pager.page_token': isLoadMore ? pageToken.current : undefined,
          parent_id: search ? rootFolder?.id : urlFolderId, // 搜索时全局搜，而不是搜当前目录
          ratio: filters.ratios,
          resolution: filters.resolutions,
          search_keyword: search,
          sort_by: sortBy,
          task_type: transformTaskTypes(filters.taskTypes),
        },
      }),
    {
      manual: true,
      debounceWait: 100,
      onSuccess: ({ data, error }, [isLoadMore]) => {
        if (!isLoadMore) {
          setHasLoadedInitial(true);
        }
        if (error) {
          // 如果是访问别人的文件夹 / 不存在的文件夹，则跳转到根文件夹
          if (!isLoadMore && urlFolderId) {
            router.replace('/assets/mycreations');
          } else {
            setError(error.message);
          }
          return;
        }

        if (data) {
          const { nodes, next_page_token, parents } = data;
          setCurrentFolderId(urlFolderId || parents[0].id);

          // 将新节点转换为 FileItem 格式
          const newFiles: FileItem[] = nodes.map(createFileItem);

          // 更新文件列表
          if (isLoadMore) {
            setFiles((prev) => [...prev, ...newFiles]);
          } else {
            setFiles(newFiles);
          }

          // 更新分页状态
          pageToken.current = next_page_token;
          setHasMore(!!next_page_token && newFiles.length === pageSize);

          // 更新面包屑导航
          if (!isLoadMore && parents) {
            const breadcrumbItems: BreadcrumbItem[] = [
              ...parents.map((parent, idx) => ({
                id: parent.id,
                name: idx === 0 ? t('creations.folders.root') : parent.name, // 面包屑第一层是根文件夹，名字需要多语言展示（用户不可改名）
              })),
            ];
            setBreadcrumbs(breadcrumbItems);
          }
        }
      },
    },
  );

  // 当依赖项变化时重新加载数据
  useEffect(() => {
    loadFiles(false);
  }, [urlFolderId, search, sortBy, order, filters, teamSpaceId]);

  const fetchDetailPage = useMemoizedFn(async (detailPageToken?: string) => {
    if (!detailPageToken) {
      return {
        files: [] as FileItem[],
        hasMore: false,
        nextPageToken: undefined,
      };
    }

    const { data, error } = await fetchFileList({
      query: {
        success_only: true,
        'created_at.from': formatStartDateForApi(filters.dateRange?.from),
        'created_at.to': formatEndDateForApi(filters.dateRange?.to),
        owner_id: filters.creatorIds?.[0],
        filter_type: filters.fileTypes,
        is_marked: filters.filterMark,
        model_version: filters.modelVersions,
        order_by: sortBy === 'last_open_at' ? 'newest' : order,
        'pager.pagesz': pageSize,
        'pager.page_token': detailPageToken,
        parent_id: search ? rootFolder?.id : urlFolderId,
        ratio: filters.ratios,
        resolution: filters.resolutions,
        search_keyword: search,
        sort_by: sortBy,
        task_type: transformTaskTypes(filters.taskTypes),
      },
    });

    if (error || !data) {
      return {
        files: [] as FileItem[],
        hasMore: true,
        nextPageToken: detailPageToken,
      };
    }

    const nextFiles: FileItem[] = data.nodes.map(createFileItem);
    return {
      files: nextFiles,
      hasMore: !!data.next_page_token && nextFiles.length === pageSize,
      nextPageToken: data.next_page_token,
    };
  });

  const { openFileById, isOpenedFile } = useOpenFile({
    files,
    initialHasMore: hasMore,
    initialPageToken: pageToken.current || undefined,
    handleCollect: ({ id, obj_type }) => {
      if (obj_type !== 'creation') return;
      setFiles((prev) =>
        prev.map((file) => {
          const matchedCreationId = file.creationId || file.id;
          if (file.type !== 'creation' || matchedCreationId !== id) return file;
          return { ...file, is_favorite: true };
        }),
      );
    },
    handleUnCollect: ({ id, obj_type }) => {
      if (obj_type !== 'creation') return;
      setFiles((prev) =>
        prev.map((file) => {
          const matchedCreationId = file.creationId || file.id;
          if (file.type !== 'creation' || matchedCreationId !== id) return file;
          return { ...file, is_favorite: false };
        }),
      );
    },
    handleVote: ({ id, obj_type, voteType }) => {
      if (obj_type !== 'creation' || !voteType) return;
      setFiles((prev) =>
        prev.map((file) => {
          const matchedCreationId = file.creationId || file.id;
          if (file.type !== 'creation' || matchedCreationId !== id) return file;
          return { ...file, vote: voteType };
        }),
      );
    },
    handleUnVote: ({ id, obj_type }) => {
      if (obj_type !== 'creation') return;
      setFiles((prev) =>
        prev.map((file) => {
          const matchedCreationId = file.creationId || file.id;
          if (file.type !== 'creation' || matchedCreationId !== id) return file;
          return { ...file, vote: 'unspecified' };
        }),
      );
    },
    fetchDetailPage,
  });

  // 导航到指定文件夹
  const navigateToFolder = useMemoizedFn((folderId: string) => {
    if (folderId === rootFolder?.id) {
      router.push('/assets/mycreations');
    } else {
      router.push(`/assets/mycreations/folder/${folderId}`);
    }
  });

  // 打开文件的处理函数
  const handleFileOpen = useMemoizedFn((item: FileItem) => {
    if (item.type === 'folder') {
      // 文件夹处理：导航到该文件夹
      navigateToFolder(item.id);
    } else if (item.creationId) {
      openFileById(item.creationId);
    } else {
      toast({
        message: 'Invalid file type',
      });
    }
  });

  // 文件状态更新方法，用于支持文件操作后的本地状态更新
  const updateFiles = useMemoizedFn((updater: (files: FileItem[]) => FileItem[]) => {
    setFiles((prevFiles) => {
      const updatedFiles = updater(prevFiles);

      // 如果更新后长度变少了，而且小于一页的数量，就重新请求接口
      if (updatedFiles.length < prevFiles.length && updatedFiles.length < pageSize) {
        // 使用 setTimeout 避免在状态更新过程中再次触发状态更新
        setTimeout(() => {
          loadFiles(false);
        }, 0);
      }

      return updatedFiles;
    });
  });

  return {
    // 状态
    files,
    currentFiles: files,
    currentFolderId,
    breadcrumbs,
    isLoading,
    hasMore,
    error,
    rootFolder,
    isOpenedFile,
    hasLoadedInitial,

    // 方法
    navigateToFolder,
    handleFileOpen,
    openCreationDetailById: openFileById,
    updateFiles,
    loadMore: () => loadFiles(true),
  };
}
